// client/src/pages/VerifyEmail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = (params.get("token") || "").trim();
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("Verifying your email...");
  const [user, setUser] = useState(null);

  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, "");
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  useEffect(() => {
    let cancelled = false;
    let redirectTimer;

    async function verify() {
      if (!token) {
        setStatus("error");
        setMessage("This verification link is missing its token.");
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.message || data?.error || "Could not verify email.");
        }
        if (cancelled) return;

        if (data?.token) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("ga_token", data.token);
          localStorage.setItem("authToken", data.token);
        }
        if (data?.user) {
          localStorage.setItem("ga_user", JSON.stringify(data.user));
          localStorage.setItem("user", JSON.stringify(data.user));
          if (data.user.email) {
            localStorage.setItem("member_email", String(data.user.email).trim().toLowerCase());
          }
        }

        window.dispatchEvent(new Event("auth:changed"));
        setUser(data?.user || null);
        setStatus("success");
        setMessage(data?.message || "Email verified. Welcome to GAStreaming.");

        const username = data?.user?.username;
        if (username) {
          redirectTimer = window.setTimeout(() => {
            navigate(`/member/${encodeURIComponent(username)}`, { replace: true });
          }, 1800);
        }
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(err?.message || "Could not verify email.");
      }
    }

    verify();
    return () => {
      cancelled = true;
      if (redirectTimer) window.clearTimeout(redirectTimer);
    };
  }, [API_BASE, navigate, token]);

  return (
    <div className="ga-verify-wrap">
      <style>{`
        .ga-verify-wrap{min-height:calc(100vh - 140px);display:flex;align-items:center;justify-content:center;padding:32px 16px}
        .ga-verify-card{width:100%;max-width:680px;border-radius:20px;padding:26px;background:radial-gradient(800px 360px at 20% 0%,rgba(255,0,0,.16),transparent 58%),rgba(0,0,0,.72);border:1px solid rgba(255,0,0,.28);box-shadow:0 18px 60px rgba(0,0,0,.58);color:#fff}
        .ga-verify-kicker{font-size:12px;font-weight:900;letter-spacing:1.5px;text-transform:uppercase;color:#ff747c;margin-bottom:10px}
        .ga-verify-card h1{margin:0 0 10px;font-family:Georgia,"Times New Roman",serif;font-size:clamp(32px,6vw,48px)}
        .ga-verify-card p{margin:0;color:rgba(255,255,255,.78);line-height:1.65}
        .ga-verify-status{margin:20px 0;padding:14px 16px;border-radius:14px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12)}
        .ga-verify-status.success{border-color:rgba(35,180,90,.45);background:rgba(35,180,90,.10)}
        .ga-verify-status.error{border-color:rgba(255,60,60,.45);background:rgba(255,0,0,.10)}
        .ga-verify-actions{display:flex;gap:10px;flex-wrap:wrap;margin-top:18px}
        .ga-verify-link{display:inline-flex;align-items:center;justify-content:center;min-height:44px;padding:0 18px;border-radius:999px;text-decoration:none;font-weight:900;color:#fff;border:1px solid rgba(255,0,0,.35);background:linear-gradient(180deg,#e10613,#8c0000)}
        .ga-verify-link.secondary{background:rgba(255,255,255,.07);border-color:rgba(255,255,255,.15)}
      `}</style>

      <div className="ga-verify-card">
        <div className="ga-verify-kicker">GAStreaming Account Security</div>
        <h1>
          {status === "success"
            ? "Email verified"
            : status === "error"
            ? "Verification problem"
            : "Verifying email"}
        </h1>
        <div className={`ga-verify-status ${status}`}>
          <p>{message}</p>
        </div>

        {status === "success" && user?.username ? (
          <div className="ga-verify-actions">
            <Link className="ga-verify-link" to={`/member/${encodeURIComponent(user.username)}`}>
              Continue to my profile
            </Link>
          </div>
        ) : null}

        {status === "error" ? (
          <div className="ga-verify-actions">
            <Link className="ga-verify-link" to="/register">Register again</Link>
            <Link className="ga-verify-link secondary" to="/login">Go to sign in</Link>
          </div>
        ) : null}
      </div>
    </div>
  );
}
